import { IconButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { MoreHorizontal as MoreHorizontalIcon, Download as DownloadIcon } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { Tag, TagVariant } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { fetchMPSVenuePayoutInCsv, fetchSalesReportList } from 'apis/merchant-payout-service/api';
import { VenuePayout, PayoutStatus } from 'apis/merchant-payout-service/types';
import {
  generatePayoutReportCsv,
  getPayoutReportCsvLink,
  getPdfLink,
} from 'apis/restaurant-api/merchant-admin/payouts/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useFetch } from 'common/hooks';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { downloadFileFromAxiosResponse, downloadFromUrl } from 'common/utils/download';
import { countryToTimezone } from 'locale';

import LocalizedMonetaryAmount from './LocalizedMonetaryAmount';
import { PayoutMpsReportListModal } from './PayoutMpsReportListModal';
import { PayoutReportListModal } from './PayoutReportListModal';
import { reportPayoutReportCsvDownload, reportPayoutReportDownload } from './behavioral';
import { isMpsPayoutReport } from './utils';

type Props = {
  venue: Venue;
};

const intervalTranslations = {
  custom: 'payout-reports.custom',
  hexa_monthly: 'payout-reports.hexa-monthly',
  monthly: 'payout-reports.monthly',
  semi_monthly: 'payout-reports.semi-monthly',
  semi_weekly: 'payout-reports.semi-weekly',
  weekly: 'payout-reports.weekly',
};

const payoutStatuses: { [key in PayoutStatus]: string } = {
  in_progress: 'payout-reports.statuses.in_progress',
  sent_to_bank: 'payout-reports.statuses.sent_to_bank',
  kyc_failed: 'payout-reports.statuses.kyc_failed',
  not_applicable: 'payout-reports.statuses.not_applicable',
};

const payoutStatusTagVariants: { [key in PayoutStatus]: TagVariant } = {
  in_progress: 'lightYellow',
  sent_to_bank: 'lightBlue',
  kyc_failed: 'lightRed',
  not_applicable: 'lightBlue',
};

const ActionsContainer = styled.div`
  min-width: 120px;
  padding: 8px;
  border-radius: 8px;
  overflow: hidden;
`;

const ActionsMenuItem = styled(MenuActionItem).attrs({ variant: 'black', icon: <DownloadIcon /> })`
  margin-block-start: 8px;
  margin-block-end: 8px;
`;

const PayoutReportList = ({ venue }: Props) => {
  const intl = useIntl();
  const { data = [], error, loading } = useFetch(() => fetchSalesReportList(venue.id), [venue.id]);
  const [generatingCsvReport, setGeneratingCsvReport] = useState<boolean>(false);

  const [payoutReport, setPayoutReport] = useState<VenuePayout | null>(null);
  const t = (id: string) => intl.formatMessage({ id });

  const sortedData = useMemo(
    () => [...data].sort((a, b) => DateTime.fromISO(b.startDate).toMillis() - DateTime.fromISO(a.startDate).toMillis()),
    [data],
  );

  const handleDownloadCsvFromRapi = (payoutId: string) => {
    setGeneratingCsvReport(true);
    generatePayoutReportCsv(venue.id, payoutId)
      .then(() => {
        getPayoutReportCsvLink(venue.id, payoutId)
          .then((res: { data: string }) => {
            downloadFromUrl(res.data);
            reportPayoutReportCsvDownload();
          })
          .catch(() => {
            toast.error(<FormattedMessage id="payout-reports.csv-download-error" />, { duration: Infinity });
          });
      })
      .catch(() => {
        toast.error(<FormattedMessage id="payout-reports.csv-generation-error" />, { duration: Infinity });
      })
      .finally(() => {
        setGeneratingCsvReport(false);
      });
  };

  const handleVenuePayoutCsvDownload = async (venueId: string, venuePayoutId: string, isMpsPayout: boolean) => {
    try {
      setGeneratingCsvReport(true);
      const response = await fetchMPSVenuePayoutInCsv(venueId, venuePayoutId, isMpsPayout);
      downloadFileFromAxiosResponse(response);
    } catch (_) {
      toast.error(<FormattedMessage id="payout-reports.csv-download-error" />, { duration: Infinity });
    } finally {
      setGeneratingCsvReport(false);
    }
  };

  const handleDownloadPDFClick = (payoutId: string) => {
    getPdfLink(venue.id, payoutId).then(({ data }: { data: string }) => {
      downloadFromUrl(data);
      reportPayoutReportDownload();
    });
  };

  const handleInvoiceDossierModal = (payout: VenuePayout) => {
    setPayoutReport(payout);
  };

  const handleModalClose = () => {
    setPayoutReport(null);
  };

  const isPayoutForHRVBefore2023 = (country: string, year: number) => country === 'HRV' && year < 2023;

  return (
    <>
      <DataTable<VenuePayout>
        dataSource={sortedData}
        columns={[
          {
            id: '1',
            name: t('payout-reports.report'),
            render: ({ record: payout }) => (
              <>
                {t('payout-reports.report')} {t(intervalTranslations[payout.interval])} {payout.intervalNumber}
              </>
            ),
          },
          {
            id: '2',
            name: t('payout-reports.amount'),
            render: ({ record: payout }) => {
              const endDate = DateTime.fromISO(payout.endDate).year;
              return (
                <LocalizedMonetaryAmount
                  amount={
                    isPayoutForHRVBefore2023(payout.country, endDate)
                      ? convertMonetaryIntToDecimal(payout.payoutAmount, 'HRK')
                      : convertMonetaryIntToDecimal(payout.payoutAmount, payout.currency)
                  }
                  currency={isPayoutForHRVBefore2023(payout.country, endDate) ? 'HRK' : payout.currency}
                />
              );
            },
          },
          {
            id: '3',
            name: t('payout-reports.count'),
            render: ({ record: payout }) => payout.numberOfPurchases,
          },
          {
            id: '4',
            name: t('payout-reports.time'),
            render: ({ record: payout }) => {
              const timezone = countryToTimezone(payout.country);
              const startDate = DateTime.fromISO(payout.startDate).setZone(timezone);
              const endDate = DateTime.fromISO(payout.endDate).setZone(timezone);
              return (
                <>
                  {startDate.toFormat('MMM d, y')} - {endDate.toFormat('MMM d, y')}
                </>
              );
            },
          },
          {
            id: '5',
            name: t('payout-reports.bank_account'),
            render: ({ record: payout }) => (
              <Popover
                placement="right"
                renderArrow
                content={<div>{!!payout.toAccount.length && t('payout-reports.bank_account_number_tooltip')}</div>}
              >
                {!!payout.toAccount.length && payout.toAccount}
              </Popover>
            ),
          },
          {
            id: '6',
            name: t('payout-reports.status'),
            render: ({ record: payout }) => (
              <Tag variant={payoutStatusTagVariants[payout.transferStatus]} uppercase>
                {t(payoutStatuses[payout.transferStatus])}
              </Tag>
            ),
          },
          {
            id: '7',
            name: '',
            alignContent: 'right',
            render: ({ record: payout }) => (
              <div data-testid="download-popover">
                <Popover
                  placement="left"
                  showOnClick
                  showOnHover={false}
                  closeOnClickInside
                  disableContentPadding
                  style={{ display: 'block', lineHeight: 0, maxWidth: 20, margin: 'auto' }}
                  content={
                    !payout.invoiceDossierExists && !isMpsPayoutReport(payout) ? (
                      <ActionsContainer>
                        <ActionsMenuItem
                          variant="black"
                          icon={<DownloadIcon />}
                          onClick={() =>
                            payout.year <= 2021
                              ? handleDownloadCsvFromRapi(payout.id)
                              : handleVenuePayoutCsvDownload(payout.venue, payout.id, isMpsPayoutReport(payout))
                          }
                        >
                          <FormattedMessage id="payout-reports.download-csv" />
                        </ActionsMenuItem>
                        <ActionsMenuItem
                          variant="black"
                          icon={<DownloadIcon />}
                          onClick={() => {
                            handleDownloadPDFClick(payout.id);
                          }}
                        >
                          <FormattedMessage id="payout-reports.download-pdf" />
                        </ActionsMenuItem>
                      </ActionsContainer>
                    ) : (
                      <ActionsContainer>
                        <MenuActionItem
                          data-testid="download-button"
                          variant="black"
                          onClick={() => {
                            handleInvoiceDossierModal(payout);
                          }}
                        >
                          <FormattedMessage id="payout-reports.download-documents" />
                        </MenuActionItem>

                        <MenuActionItem
                          data-testid="download-csv-button"
                          variant="black"
                          icon={<DownloadIcon />}
                          onClick={() =>
                            handleVenuePayoutCsvDownload(payout.venue, payout.id, isMpsPayoutReport(payout))
                          }
                        >
                          <FormattedMessage id="payout-reports.download-csv" />
                        </MenuActionItem>
                      </ActionsContainer>
                    )
                  }
                >
                  <IconButton variant="lightGrey" size="small" background="animated">
                    <MoreHorizontalIcon />
                  </IconButton>
                </Popover>
              </div>
            ),
          },
        ]}
        getRowKey={payout => payout.id}
        loading={loading || generatingCsvReport}
        emptyMessage={
          error
            ? {
                title: t('state.error'),
                description: t('errors.unknown-error-retry-contact'),
              }
            : undefined
        }
        horizontalPadding="wide"
      />

      {!!payoutReport && isMpsPayoutReport(payoutReport) && (
        <PayoutMpsReportListModal intl={intl} onClose={handleModalClose} payoutReport={payoutReport} />
      )}
      {!!payoutReport && !isMpsPayoutReport(payoutReport) && (
        <PayoutReportListModal intl={intl} onClose={handleModalClose} venueId={venue.id} payoutReport={payoutReport} />
      )}
    </>
  );
};

export default PayoutReportList;
